import { novoId } from '../id'
import { useSyncExternalStore } from 'react'
import type { QueryClient } from '@tanstack/react-query'
import { MAXIMO_POR_PEDIDO_EM_MASSA, type AcaoEmMassaEntrada } from '@crm/shared'
import { api, ErroRequisicao } from '../api/cliente'

/**
 * Os 15 segundos antes de uma ação em massa acontecer.
 *
 * Confirmar na janela não muda nada ainda: agenda. Durante 15 segundos a ação
 * espera aqui, com um botão de desfazer, e só então vai para a api. Desfazer é
 * simplesmente não mandar — nada precisa ser revertido no servidor, porque
 * nada chegou lá.
 *
 * Em lote isso importa mais do que em qualquer outro lugar: transferir cem
 * leads para a pessoa errada só se descobre quando o vendedor procura a
 * própria carteira e ela não está lá.
 *
 * **Mora num módulo, e não no componente da barra.** A barra some assim que a
 * seleção é limpa, e ela é limpa na confirmação. Se o relógio morasse lá, a
 * ação morreria junto, calada, sem acontecer nem ser desfeita. Aqui ela
 * sobrevive à barra, à troca de página e à troca de visão entre quadro e
 * lista — é o mesmo desenho da fila de mensagens.
 *
 * Fechar a aba ou sair do sistema com uma ação esperando: ela acontece na
 * hora. Quem confirmou e foi embora não vai voltar para desfazer, e perder em
 * silêncio uma ação confirmada é pior do que perder a chance de desistir.
 */

export const SEGUNDOS_PARA_DESFAZER = 15

/** Quanto tempo o aviso de "feito" fica na tela antes de sair sozinho. */
const MOSTRAR_FEITO_MS = 3000

export type EstadoDaAcao = 'aguardando' | 'executando' | 'feita' | 'falhou'

export interface AcaoAgendada {
  id: string
  dados: AcaoEmMassaEntrada
  /** O que vai acontecer, em uma frase: "Transferir 3 lead(s) para Ana". */
  resumo: string
  /** Quando acontece, em milissegundos desde a época. */
  aconteceEm: number
  estado: EstadoDaAcao
  erro: string | null
}

let agendadas: AcaoAgendada[] = []
const ouvintes = new Set<() => void>()
const relogios = new Map<string, number>()
let consultas: QueryClient | null = null

function publicar(novas: AcaoAgendada[]) {
  agendadas = novas
  for (const ouvinte of ouvintes) ouvinte()
}

function atualizar(id: string, mudanca: Partial<AcaoAgendada>) {
  publicar(agendadas.map((a) => (a.id === id ? { ...a, ...mudanca } : a)))
}

function retirar(id: string) {
  const relogio = relogios.get(id)
  if (relogio !== undefined) window.clearTimeout(relogio)
  relogios.delete(id)
  publicar(agendadas.filter((a) => a.id !== id))
}

/**
 * A ação repartida em pedidos que a api aceita.
 *
 * O "Marcar tudo" dos Contatos pode juntar milhares de ids, e cada pedido leva
 * no máximo `MAXIMO_POR_PEDIDO_EM_MASSA`. Seleção pequena sai num pedido só,
 * como sempre saiu.
 */
function emPedidos(dados: AcaoEmMassaEntrada): AcaoEmMassaEntrada[] {
  const pedidos: AcaoEmMassaEntrada[] = []
  for (let i = 0; i < dados.ids.length; i += MAXIMO_POR_PEDIDO_EM_MASSA) {
    pedidos.push({ ...dados, ids: dados.ids.slice(i, i + MAXIMO_POR_PEDIDO_EM_MASSA) })
  }
  return pedidos
}

/** A fila precisa do cliente de consultas para o quadro e a lista se atualizarem depois. */
export function configurarFilaDeAcoes(cliente: QueryClient) {
  consultas = cliente
}

export function agendarAcaoEmMassa(dados: AcaoEmMassaEntrada, resumo: string): string {
  const id = novoId()
  publicar([
    ...agendadas,
    {
      id,
      dados,
      resumo,
      aconteceEm: Date.now() + SEGUNDOS_PARA_DESFAZER * 1000,
      estado: 'aguardando',
      erro: null,
    },
  ])
  relogios.set(
    id,
    window.setTimeout(() => void executar(id), SEGUNDOS_PARA_DESFAZER * 1000),
  )
  return id
}

/**
 * Desfaz, se ainda der tempo.
 *
 * Depois que o pedido saiu não há volta por aqui: ele pode já ter sido
 * gravado, e fingir que desfez seria pior do que dizer que já foi.
 */
export function desfazerAcao(id: string): boolean {
  const acao = agendadas.find((a) => a.id === id)
  if (!acao || acao.estado !== 'aguardando') return false
  retirar(id)
  return true
}

/** Tira da tela um aviso de falha ou de "feito". */
export function dispensarAcao(id: string) {
  const acao = agendadas.find((a) => a.id === id)
  if (acao && acao.estado !== 'aguardando' && acao.estado !== 'executando') retirar(id)
}

async function executar(id: string) {
  relogios.delete(id)
  const acao = agendadas.find((a) => a.id === id)
  if (!acao || acao.estado !== 'aguardando') return

  atualizar(id, { estado: 'executando' })
  const pedidos = emPedidos(acao.dados)
  let feitos = 0
  try {
    for (const pedido of pedidos) {
      await api('/api/negociacoes/em-massa', { metodo: 'POST', corpo: pedido })
      feitos += pedido.ids.length
    }
    atualizar(id, { estado: 'feita' })
    relogios.set(
      id,
      window.setTimeout(() => retirar(id), MOSTRAR_FEITO_MS),
    )
    if (consultas) {
      void consultas.invalidateQueries({ queryKey: ['contatos'] })
      void consultas.invalidateQueries({ queryKey: ['negociacoes'] })
    }
  } catch (erro) {
    /*
      A falha fica na tela até alguém fechar.

      A pessoa confirmou há 15 segundos e já pode estar em outra coisa. Sumir
      com o aviso sozinho deixaria a impressão de que a transferência foi feita
      — e quem descobre que não foi é o vendedor que não recebeu os clientes.
    */
    const motivo =
      erro instanceof ErroRequisicao ? erro.corpo.mensagem : 'Não foi possível concluir.'
    atualizar(id, {
      estado: 'falhou',
      // Parou no meio de uma seleção grande: dizer quanto já foi, para ninguém
      // repetir a ação inteira achando que nada aconteceu.
      erro:
        feitos > 0
          ? `Parou no meio: ${feitos} de ${acao.dados.ids.length} já foram feitos. ${motivo}`
          : motivo,
    })
    if (feitos > 0 && consultas) {
      void consultas.invalidateQueries({ queryKey: ['contatos'] })
      void consultas.invalidateQueries({ queryKey: ['negociacoes'] })
    }
  }
}

/** Executa na hora o que ainda esperava. Usado antes de sair do sistema. */
export async function executarAcoesAgora(): Promise<void> {
  const esperando = agendadas.filter((a) => a.estado === 'aguardando')
  await Promise.allSettled(
    esperando.map((a) => {
      const relogio = relogios.get(a.id)
      if (relogio !== undefined) window.clearTimeout(relogio)
      return executar(a.id)
    }),
  )
}

/*
  Fechar a aba ou recarregar: o que esperava acontece na hora.

  `keepalive` deixa o pedido terminar depois que a página já foi embora; um
  `fetch` comum seria cortado junto com ela.
*/
if (typeof window !== 'undefined') {
  window.addEventListener('pagehide', () => {
    for (const acao of agendadas.filter((a) => a.estado === 'aguardando')) {
      const relogio = relogios.get(acao.id)
      if (relogio !== undefined) window.clearTimeout(relogio)
      relogios.delete(acao.id)
      for (const pedido of emPedidos(acao.dados)) {
        void fetch('/api/negociacoes/em-massa', {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify(pedido),
          keepalive: true,
        }).catch(() => undefined)
      }
    }
  })
}

function assinar(ouvinte: () => void) {
  ouvintes.add(ouvinte)
  return () => ouvintes.delete(ouvinte)
}

export function useAcoesAgendadas(): AcaoAgendada[] {
  return useSyncExternalStore(assinar, () => agendadas)
}
